"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { useMatchKickoffCountdown } from "@/hooks/useMatchKickoffCountdown";
import { getMatchDisplayTime } from "@/utils/matchInfo";
import { useTheme } from "@/context/ThemeContext";
import { MatchService } from "@/services/matchService";
import { normalizeData } from "@/utils";
import LoadingInner from "../Loading";
import { MatchInfoProvider } from "../ThongTinTranDau/MatchInfoContext";
import LiveTab from "../ThongTinTranDau/LiveTab";
import HeadToHeadTab from "../ThongTinTranDau/HeadToHeadTab";
import RecentFormTab from "../ThongTinTranDau/RecentFormTab";
import OddsTab from "../ThongTinTranDau/OddTab";
import tipsAiModalBgTop from "@/assets/images/viugo/tips-ai-modal-bg-top.jpg";
import tipsAiLoadingImage from "@/assets/images/viugo/kbv-v16-tips-ai-loading.webp";
import tipsModalCheck from "@/assets/images/viugo/kbv-v16-tips-modal-check.svg";
import tipsModalFootballRed from "@/assets/images/viugo/kbv-v16-tips-modal-football-red.svg";
import tipsModalFootballBlue from "@/assets/images/viugo/kbv-v16-tips-modal-football-blue.svg";
import tipsModalAnalysis from "@/assets/images/viugo/kbv-v16-tips-modal-analysis.svg";
import tipsAiPredictIcon from "@/assets/images/viugo/kbv-v16-tips-ai-predict-icon.svg";
import {
    buildFormText,
    countWins,
    formatHandicap,
    normalizeOverUnderPick,
    normalizePick,
    parseOverviewBlocks,
} from "./helpers";

import "../ViuGoOddsMatchRow/ViuGoOddsMatchTime.css";

const TZ = "Asia/Ho_Chi_Minh";
dayjs.extend(utc);
dayjs.extend(timezone);

const INFO_TABS = [
    { id: "ai", label: "AI Dự Đoán" },
    { id: "live", label: "Trực tiếp" },
    { id: "h2h", label: "Lịch sử đối đầu" },
    { id: "form", label: "Thành tích gần đây" },
    { id: "odds", label: "So sánh kèo" },
];

function makeLeagueShortName(name) {
    if (!name) return "CON CLA";
    const words = String(name)
        .toUpperCase()
        .split(/\s+/)
        .filter(Boolean);

    if (words.length >= 2) {
        return `${words[0].slice(0, 3)} ${words[1].slice(0, 3)}`;
    }

    if (words[0].length >= 6) {
        return `${words[0].slice(0, 3)} ${words[0].slice(3, 6)}`;
    }

    return words[0];
}

function AiPredictIcon({ className = "" }) {
    return (
        <img
            src={tipsAiPredictIcon}
            alt=""
            width={31}
            height={30}
            className={className}
            aria-hidden
        />
    );
}

function TipsModalCloseIcon({ className = "" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={24}
            height={24}
            viewBox="0 0 24 24"
            fill="none"
            className={className}
            aria-hidden
        >
            <path
                d="M11.9987 10.5865L16.9485 5.63672L18.3627 7.05093L13.4129 12.0007L18.3627 16.9504L16.9485 18.3646L11.9987 13.4149L7.04899 18.3646L5.63477 16.9504L10.5845 12.0007L5.63477 7.05093L7.04899 5.63672L11.9987 10.5865Z"
                fill="currentColor"
            />
        </svg>
    );
}

export default function ViuGoTipsAiModal({
    open,
    onClose,
    match: initialMatch,
    matchInfo,
    statsInfo,
    oddsInfo,
    leagueName,
    leagueLogo,
    homeName,
    homeLogo,
    awayName,
    awayLogo,
    matchDate,
    matchTime,
    matchTimestamp,
    headerScore,
    liveScoreToday,
    matchStatusNum,
    matchTimeUnix,
    halfStartTime,
    isMatchLive = false,
    statusLabel,
    insightData,
    insightLoading,
    initialActiveTab,
}) {
    const { theme } = useTheme();
    const kbvData = insightData?.kbvData || {};
    const insight = insightData?.insight || {};
    const scoreText = headerScore || "0 - 0";
    const safeHomeName = homeName || "Deportes Tolima";
    const safeAwayName = awayName || "Universitario de Deportes";
    const safeLeagueName = kbvData?.leagueData?.name || leagueName || "Copa Libertadores";
    const leagueShortName = makeLeagueShortName(safeLeagueName);
    const safeDate = matchDate || "08/04";
    const safeTime = matchTime || "09:00";
    const safeStatus = statusLabel || "Hiệp 2";
    const safeStatusLower = String(safeStatus || "").toLowerCase();
    const shouldShowHeaderScore = !(safeStatusLower.includes("sắp diễn ra") || safeStatusLower.includes("chưa"));
    const showHeaderScoreBlock = isMatchLive || shouldShowHeaderScore;

    const [headerClockTick, setHeaderClockTick] = useState(0);
    const [activeTab, setActiveTab] = useState("ai");
    const [infoData, setInfoData] = useState(null);
    const [infoLoading, setInfoLoading] = useState(false);

    useEffect(() => {
        if (!open || !isMatchLive) return undefined;
        const id = window.setInterval(() => setHeaderClockTick((n) => n + 1), 1000);
        return () => window.clearInterval(id);
    }, [open, isMatchLive]);

    const headerDisplayTime = useMemo(
        () =>
            getMatchDisplayTime(
                liveScoreToday || undefined,
                Number(matchStatusNum),
                matchTimeUnix,
                halfStartTime,
            ),
        [liveScoreToday, matchStatusNum, matchTimeUnix, halfStartTime, headerClockTick],
    );

    useEffect(() => {
        if (!open) return;
        setActiveTab(initialActiveTab || "ai");
    }, [open, initialActiveTab]);

    useEffect(() => {
        if (!open) return;
        if (activeTab === "ai") return;
        if (!initialMatch?.id) return;

        let cancelled = false;
        setInfoData(null);
        setInfoLoading(true);

        const { id: matchId, homeId, awayId } = initialMatch;
        const isLiveFromInfo = !!matchInfo?.isLive;

        Promise.all([
            MatchService.getMatchById(matchId),
            MatchService.getMatchAnalysis(matchId),
            MatchService.getLiveOddByMatchId(matchId),
            MatchService.getOddByMatchId(matchId),
            MatchService.getMatchComparison(matchId),
            MatchService.getMatchGoalStats(matchId),
            MatchService.getMatchOddStats(matchId),
            MatchService.getMatchFormStats(matchId, `${homeId},${awayId}`),
            MatchService.getMatchLatest(matchId, homeId, awayId),
            MatchService.getMatchLineups(matchId, homeId, awayId),
            MatchService.getHeatMapData(isLiveFromInfo, {
                isLive: isLiveFromInfo,
                homeName: normalizeData(initialMatch?.homeTeam?.name || initialMatch?.homeName),
                awayName: normalizeData(initialMatch?.awayTeam?.name || initialMatch?.awayName),
                date: dayjs.unix(initialMatch?.matchTime).tz(TZ).format("YYYY-MM-DD"),
            }),
        ])
            .then(
                ([
                    matchRes,
                    matchAnalysis,
                    liveOddData,
                    oddData,
                    matchComparison,
                    matchGoalStats,
                    matchOddStats,
                    matchFormStats,
                    matchLatest,
                    matchLineups,
                    heatMapLinkLive,
                ]) => {
                    if (cancelled) return;
                    const match =
                        matchRes?.success && matchRes?.data ? matchRes.data : initialMatch;
                    setInfoData({
                        match,
                        matchAnalysis,
                        liveOddData: liveOddData || {},
                        oddData: oddData || {},
                        matchComparison: matchComparison || {},
                        matchGoalStats: matchGoalStats || {},
                        matchOddStats: matchOddStats || {},
                        matchFormStats: matchFormStats || {},
                        matchLatest: matchLatest || {},
                        lineup: matchLineups || {},
                        heatMapLinkLive: heatMapLinkLive || null,
                    });
                },
            )
            .catch(() => {
                if (!cancelled) setInfoData({ match: initialMatch });
            })
            .finally(() => {
                if (!cancelled) setInfoLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, activeTab, initialMatch?.id, matchInfo?.isLive]);

    const { headerLine1, headerLine2, headerLiveMinuteTick } = useMemo(() => {
        if (!isMatchLive) {
            return {
                headerLine1: safeDate,
                headerLine2: safeTime,
                headerLiveMinuteTick: false,
            };
        }
        const dt = headerDisplayTime;
        if (dt?.type === "minute") {
            const half = dt.halfLabel && dt.halfLabel !== "--" ? dt.halfLabel : safeStatus;
            return {
                headerLine1: half,
                headerLine2: String(dt.content ?? ""),
                headerLiveMinuteTick: true,
            };
        }
        if (dt?.type === "var") {
            return {
                headerLine1: safeDate,
                headerLine2: dt.content ?? "VAR",
                headerLiveMinuteTick: false,
            };
        }
        return {
            headerLine1: safeDate,
            headerLine2: dt?.content ?? safeTime,
            headerLiveMinuteTick: false,
        };
    }, [isMatchLive, headerDisplayTime, safeDate, safeTime, safeStatus]);
    const predictedHomeScore = insight?.home_score_predict ?? 1;
    const predictedAwayScore = insight?.away_score_predict ?? 1;
    const ph = Number(predictedHomeScore);
    const pa = Number(predictedAwayScore);
    const predictionText = `${safeHomeName} ${predictedHomeScore} - ${predictedAwayScore} ${safeAwayName}`;

    const asianFallback = formatHandicap(kbvData?.handicapOdds?.[0]?.instantHandicap) || `${safeAwayName} +0.5`;
    const asianPick = normalizePick(insight?.handicap_ai_content, asianFallback);
    const overUnderPick = normalizeOverUnderPick(insight?.over_under_ai_content, "Xỉu");
    const euroPick = normalizePick(insight?.euro_ai_content, "Hòa");

    const homeFormText = buildFormText(kbvData?.homeLastMatches, kbvData?.homeId, safeHomeName);
    const awayFormText = buildFormText(kbvData?.awayLastMatches, kbvData?.awayId, safeAwayName);
    const homeWinCount = countWins(kbvData?.homeLastMatches, kbvData?.homeId);
    const awayWinCount = countWins(kbvData?.awayLastMatches, kbvData?.awayId);
    const mentalityText =
        awayWinCount > homeWinCount
            ? `${safeAwayName} có sự tự tin cao sau chuỗi thành tích tốt và chiến thắng gần nhất. ${safeHomeName} cũng giữ được sự ổn định nhưng không thắng ở trận vừa qua.`
            : `${safeHomeName} đang duy trì sự tự tin với chuỗi kết quả tích cực gần đây. ${safeAwayName} vẫn giữ được sự ổn định và luôn tiềm ẩn khả năng tạo đột biến.`;

    const headToHeadMatches = Array.isArray(kbvData?.headToHead) ? kbvData.headToHead : [];
    const headToHeadPrefix = headToHeadMatches.length
        ? `Hai đội đã gặp nhau ${headToHeadMatches.length} trận gần đây.`
        : "Không có dữ liệu về lịch sử đối đầu giữa hai đội.";
    const headToHeadText = `${headToHeadPrefix} Gợi ý kèo: Châu Á: ${asianPick} ; Châu Âu: ${euroPick} ; Tài-Xỉu: O/U 2 -> ${overUnderPick}`;
    const overviewBlocks = parseOverviewBlocks(insight?.overview_ai_content);
    const showLoadingState = insightLoading && !insightData;
    const { countdownDisplay } = useMatchKickoffCountdown(matchTimestamp, {
        fallbackUnix: kbvData?.matchTime,
        isLive: isMatchLive,
        enabled: open,
    });

    const [isMobileViewport, setIsMobileViewport] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 767px)").matches;
    });

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mediaQuery = window.matchMedia("(max-width: 767px)");
        const updateViewport = (event) => setIsMobileViewport(event.matches);
        setIsMobileViewport(mediaQuery.matches);

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", updateViewport);
            return () => mediaQuery.removeEventListener("change", updateViewport);
        }

        mediaQuery.addListener(updateViewport);
        return () => mediaQuery.removeListener(updateViewport);
    }, []);

    const mobilePanelStyle = isMobileViewport
        ? {
              top: "12%",
              bottom: "16px",
              left: "2.5%",
              right: "2.5%",
          }
        : undefined;

    if (!open) return null;

    const modalContent = (
        <div className="keobongvip-root fixed inset-0 z-[99999999] !bg-transparent" data-theme={theme}>
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            <div
                className="kbv-bg-modal-surface-indigo-slate fixed z-[99999999] flex flex-col overflow-hidden rounded-[12px] pb-0 md:absolute md:bottom-0 md:left-1/2 md:right-auto top-[5%] md:top-0 md:my-auto md:h-[800px] md:w-[800px] md:max-w-[95%] md:-translate-x-1/2 md:rounded-[12px] md:pb-5 [&::-webkit-scrollbar]:w-1"
                style={mobilePanelStyle}
            >
                <div className="z-[9] h-[240px] shrink-0 md:sticky md:top-0">
                    <div
                        className="relative min-h-[200px] w-full overflow-hidden rounded-t-[10px] bg-cover bg-no-repeat text-center text-white"
                        style={{ backgroundImage: `url(${tipsAiModalBgTop})` }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            className="kbv-tips-ai-modal-close absolute right-[11px] top-[5px] z-20 flex h-9 w-9 cursor-pointer items-center justify-center rounded-md !border-0 !p-0 text-white md:right-[15px]"
                            aria-label="Đóng modal"
                        >
                            <TipsModalCloseIcon className="h-6 w-6" />
                        </button>

                        <div className="kbv-bg-modal-overlay-indigo-slate absolute inset-0 z-[1] opacity-60" />

                        <div className="kbv-bg-primary-overlay-indigo-slate relative z-[2] flex h-[48px] items-center justify-center gap-[5px]">
                            <div>{leagueShortName}</div>
                        </div>

                        <div className="relative z-[2] flex items-center justify-center px-[10px] md:px-0">
                            <div className="mt-[30px] w-[calc(50%_-_100px)]">
                                <img src={homeLogo} width="60" height="60" alt={safeHomeName} loading="lazy" className="mx-auto h-10 w-10 md:h-[60px] md:w-[60px]" />
                                <div className="mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-bold leading-6">{safeHomeName}</div>
                            </div>

                            <div className="flex w-[200px] flex-col items-center justify-center pt-[15px] mb-2 md:gap-1">
                                {!isMatchLive && (
                                    <div className="text-[20px] leading-4 text-white">{headerLine1}</div>
                                )}
                                {(!isMatchLive || headerLiveMinuteTick) && (
                                    <div
                                        className={`flex flex-wrap items-baseline justify-center gap-0.5 text-center text-[30px] text-white ${!isMatchLive ? "mt-2" : ""}`}
                                    >
                                        {headerLiveMinuteTick ? (
                                            <>
                                                <span className="tabular-nums">{headerLine2}</span>
                                                <span className="viugo-odds-match-time__live-minus inline-block !left-0 text-[30px] leading-none">
                                                    &apos;
                                                </span>
                                            </>
                                        ) : (
                                            headerLine2
                                        )}
                                    </div>
                                )}
                                {showHeaderScoreBlock && (
                                    <div className="text-[30px] font-bold text-white">{scoreText}</div>
                                )}
                                {!isMatchLive && countdownDisplay && (
                                    <div className="my-[5px] mx-auto h-[22px] w-max rounded-[11px] bg-white/20 px-3 text-[14px] font-semibold leading-[22px] text-white">
                                        {countdownDisplay}
                                    </div>
                                )}
                                <div className="kbv-bg-status-badge-indigo-slate w-max rounded-[12px] px-3 py-[2px] text-[14px] text-white">{safeStatus}</div>
                            </div>

                            <div className="mt-[30px] w-[calc(50%_-_100px)]">
                                <img src={awayLogo} width="60" height="60" alt={safeAwayName} loading="lazy" className="mx-auto h-10 w-10 md:h-[60px] md:w-[60px]" />
                                <div className="mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] font-bold leading-6">{safeAwayName}</div>
                            </div>
                        </div>
                    </div>

                    <div className="w-full overflow-auto">
                        <div className="flex min-w-max items-center justify-center gap-4 border-b border-[var(--theme-border)] bg-[var(--theme-bg)] px-[10px] text-[14px] font-bold leading-10 md:min-w-[436px] md:justify-center md:gap-[30px] md:px-0">
                            {INFO_TABS.map((t) => {
                                const isActive = activeTab === t.id;
                                const base =
                                    "flex shrink-0 items-center gap-[5px] whitespace-nowrap px-[5px] md:px-0 transition-colors cursor-pointer select-none";
                                const activeCls = "text-[var(--theme-primary)]";
                                const inactiveCls = "text-[var(--theme-text-muted)] hover:text-[var(--theme-primary)]";
                                return (
                                    <div
                                        key={t.id}
                                        onClick={() => setActiveTab(t.id)}
                                        role="button"
                                        tabIndex={0}
                                        className={`${base} ${isActive ? activeCls : inactiveCls}`}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                setActiveTab(t.id);
                                            }
                                        }}
                                    >
                                        {t.id === "ai" && <AiPredictIcon />}
                                        <span>{t.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex-1 w-full overflow-auto rounded-none bg-white pb-[calc(env(safe-area-inset-bottom)_+_8px)] md:h-[calc(100%_-_240px)] md:rounded-[12px] md:pb-0">
                    <div className="kbv-scrollbar-thumb-indigo-slate flex flex-col gap-[30px] p-3 [&::-webkit-scrollbar-track]:rounded-[3px] [&::-webkit-scrollbar]:h-[6px] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar]:bg-white [&::-webkit-scrollbar-thumb]:rounded-[3px]">
                        {activeTab === "ai" && (
                        <div className="flex w-full flex-wrap rounded-[12px] border-2 border-solid border-[rgba(200,211,255,1)] bg-[var(--theme-bg)]">
                            <div className="w-full rounded-[12px] bg-[var(--theme-bg)] p-[10px]">
                                <div className="kbv-bg-panel-header-indigo-slate kbv-text-panel-strong-indigo-slate relative flex h-[34px] items-center rounded-t-[10px] px-[15px] text-[15px]">
                                    <div className="absolute left-0 right-0 h-8 w-2 rounded-r-[8px] bg-[var(--theme-primary)]" />
                                    <span>AI dự đoán</span>
                                </div>

                                <div className="flex flex-col gap-[15px] pb-5 pt-5">
                                    {showLoadingState && (
                                        <div className="flex w-full items-center justify-center py-2">
                                            <img
                                                src={tipsAiLoadingImage}
                                                alt="Đang tải dữ liệu AI"
                                                width={140}
                                                height={140}
                                                className="h-[140px] w-[140px] object-contain"
                                            />
                                        </div>
                                    )}

                                    {!showLoadingState && (
                                    <>
                                    <div className="flex flex-col gap-3 md:gap-[10px]">
                                        <div className="kbv-bg-ai-gold-gradient-indigo-slate relative m-auto flex h-[26px] w-max items-center justify-center rounded-[999px] pl-9 pr-3 text-[13px] font-bold leading-none md:h-[30px] md:pl-[42px] md:pr-[15px] md:text-[15px] md:leading-[18px]">
                                            <div className="kbv-bg-ai-gold-core-indigo-slate absolute left-0 top-[-2px] h-[30px] w-[30px] rounded-[999px] md:top-[-3px] md:h-[34px] md:w-[34px]" />
                                            <AiPredictIcon className="absolute left-[-8px] top-[-7px] h-9 w-9 md:left-[-11px] md:top-[-10px] md:h-12 md:w-12" />
                                            DỰ ĐOÁN
                                        </div>

                                        <div className="my-1 px-1 text-center text-[12px] uppercase leading-[15px] tracking-[0.2px] md:my-[10px] md:px-0 md:text-[14px] md:leading-[17px] md:tracking-[0.28px]">
                                            {safeLeagueName}
                                        </div>

                                        <div className="flex items-center justify-center gap-3 md:gap-4">
                                            <div className="flex min-w-0 flex-1 justify-end">
                                                <img
                                                    src={homeLogo}
                                                    width="67"
                                                    height="67"
                                                    alt={safeHomeName}
                                                    className="h-12 w-12 object-contain md:h-[60px] md:w-[60px]"
                                                />
                                            </div>
                                            <div className="kbv-text-panel-strong-indigo-slate flex w-[4.5rem] shrink-0 items-center justify-center gap-1 text-[1.375rem] font-bold leading-tight tracking-[0.04em] md:w-[100px] md:gap-1.5 md:text-[30px] md:leading-[37px] md:tracking-[0.6px]">
                                                <span
                                                    className={
                                                        Number.isFinite(ph) &&
                                                        Number.isFinite(pa) &&
                                                        ph > pa
                                                            ? "kbv-text-prediction-winner-indigo-slate"
                                                            : ""
                                                    }
                                                >
                                                    {predictedHomeScore}
                                                </span>
                                                <span className="kbv-text-panel-strong-indigo-slate font-bold">-</span>
                                                <span
                                                    className={
                                                        Number.isFinite(ph) &&
                                                        Number.isFinite(pa) &&
                                                        pa > ph
                                                            ? "kbv-text-prediction-winner-indigo-slate"
                                                            : ""
                                                    }
                                                >
                                                    {predictedAwayScore}
                                                </span>
                                            </div>
                                            <div className="flex min-w-0 flex-1 justify-start">
                                                <img
                                                    src={awayLogo}
                                                    width="67"
                                                    height="67"
                                                    alt={safeAwayName}
                                                    className="h-12 w-12 object-contain md:h-[60px] md:w-[60px]"
                                                />
                                            </div>
                                        </div>

                                        <div className="text-center text-[11px] font-bold leading-[13px] md:text-[14px] md:leading-[17px]">
                                            {safeTime} <span className="font-normal text-[var(--theme-primary)]">| {safeDate}</span>
                                        </div>

                                        <div className="kbv-divider-light-indigo-slate relative flex items-center gap-[10px] pb-[10px] text-[14px] leading-[17px] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:content-['']">
                                            <img
                                                src={tipsModalCheck}
                                                alt=""
                                                width={22}
                                                height={22}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>
                                                <span className="font-bold">Dự đoán tỉ số: </span>
                                                <span>{predictionText}</span>
                                                <span className="text-[var(--theme-primary)]">, AI dự đoán dựa trên dữ liệu phong độ, đội hình, và lịch sử đối đầu.</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-[15px]">
                                        <div className="flex items-center gap-[10px] text-[14px] font-bold leading-[17px]">
                                            <img
                                                src={tipsModalFootballBlue}
                                                alt=""
                                                width={16}
                                                height={16}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>Kèo nhà cái tham khảo</div>
                                        </div>
                                        <div className="flex flex-wrap gap-[10px] text-[12px]">
                                            <div className="rounded-[6px] bg-black px-[10px] py-[7px] text-white">
                                                <div>
                                                    Kèo châu á: <span className="kbv-text-pick-highlight-indigo-slate">{asianPick}</span>
                                                </div>
                                            </div>
                                            <div className="rounded-[6px] bg-black px-[10px] py-[7px] text-white">
                                                <div>
                                                    Kèo Tài/Xỉu: <span className="kbv-text-pick-highlight-indigo-slate">{overUnderPick}</span>
                                                </div>
                                            </div>
                                            <div className="rounded-[6px] bg-black px-[10px] py-[7px] text-white">
                                                <div>
                                                    Kèo Châu Âu: <span className="kbv-text-pick-highlight-indigo-slate">{euroPick}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-[15px]">
                                        <div className="flex items-center gap-[10px] text-[14px] font-bold leading-[17px]">
                                            <img
                                                src={tipsModalAnalysis}
                                                alt=""
                                                width={25}
                                                height={20}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>Phân tích soi kèo</div>
                                        </div>

                                        <div className="flex items-center gap-[10px] text-[14px] leading-[17px]">
                                            <img
                                                src={tipsModalFootballRed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>
                                                <span className="text-[var(--theme-primary)]">Phong độ {safeHomeName}: </span>
                                                {homeFormText}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-[10px] text-[14px] leading-[17px]">
                                            <img
                                                src={tipsModalFootballRed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>
                                                <span className="text-[var(--theme-primary)]">Phong độ {safeAwayName}: </span>
                                                {awayFormText}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-[10px] text-[14px] leading-[17px]">
                                            <img
                                                src={tipsModalFootballRed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>
                                                <span className="text-[var(--theme-primary)]">Tâm lý: </span>
                                                {mentalityText}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-[10px] text-[14px] leading-[17px]">
                                            <img
                                                src={tipsModalFootballRed}
                                                alt=""
                                                width={16}
                                                height={16}
                                                className="shrink-0"
                                                aria-hidden
                                            />
                                            <div>
                                                <span className="text-[var(--theme-primary)]">Đối đầu: </span>
                                                {headToHeadText}
                                            </div>
                                        </div>

                                        {overviewBlocks.length > 0 && (
                                            <div className="kbv-text-panel-strong-indigo-slate flex flex-col gap-[6px] pt-[2px] text-[14px] leading-[22px]">
                                                {overviewBlocks.map((block, index) => (
                                                    <div
                                                        key={`overview-block-${index}`}
                                                        className={
                                                            block.isTitle
                                                                ? "pt-[6px] text-[15px] font-bold leading-[22px] text-[var(--theme-primary)]"
                                                                : "kbv-text-panel-strong-indigo-slate text-[14px] leading-[22px]"
                                                        }
                                                    >
                                                        {block.content}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    </>
                                    )}
                                </div>
                            </div>
                        </div>
                        )}

                        {activeTab !== "ai" && (
                            <MatchInfoProvider
                                value={{
                                    match: infoData?.match ?? initialMatch,
                                    matchAnalysis: infoData?.matchAnalysis ?? null,
                                    liveOddData: infoData?.liveOddData ?? {},
                                    oddData: infoData?.oddData ?? {},
                                    matchComparison: infoData?.matchComparison ?? {},
                                    matchGoalStats: infoData?.matchGoalStats ?? {},
                                    matchOddStats: infoData?.matchOddStats ?? {},
                                    matchFormStats: infoData?.matchFormStats ?? {},
                                    matchLatest: infoData?.matchLatest ?? {},
                                    lineup: infoData?.lineup ?? {},
                                    heatMapLinkLive: infoData?.heatMapLinkLive ?? null,
                                    matchInfo,
                                    oddsInfo,
                                    statsInfo,
                                    isLoading: infoLoading,
                                }}
                            >
                                <div className="w-full space-y-4">
                                    {infoLoading ? (
                                        <LoadingInner />
                                    ) : (
                                        <>
                                            {activeTab === "live" && <LiveTab key={initialMatch?.id} />}
                                            {activeTab === "h2h" && <HeadToHeadTab key={initialMatch?.id} />}
                                            {activeTab === "form" && <RecentFormTab key={initialMatch?.id} />}
                                            {activeTab === "odds" && <OddsTab key={initialMatch?.id} />}
                                        </>
                                    )}
                                </div>
                            </MatchInfoProvider>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (typeof document === "undefined") return modalContent;
    return createPortal(modalContent, document.body);
}
